import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth, AuthnTransaction, Tokens } from '@okta/okta-auth-js';
import OktaSignIn, { WidgetOktaAuthInterface } from '@okta/okta-signin-widget';
import { firstValueFrom, Observable } from 'rxjs';
import {
  ClearOAuthSigninRequest,
  FactorEmailToken,
  FactorInfoChange,
  FactorInfoChangeConfirm,
  SmsEnrollResponse,
} from '../../app.actions';
import { StateModel, STATE_TOKEN } from '../../app.states';
import { SupportService } from 'apps/app-cic-ciam/src/services/support.service';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import {
  faCheckCircle,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import {
  faCircleCheck,
  faCircleXmark,
} from '@fortawesome/free-regular-svg-icons';
import { EmailChangePopupComponent } from '../../popup/email-change-popup/email-change-popup.component';
import { SelfFactorService } from 'apps/app-cic-ciam/src/services/selffactor.service';
import { OktaFactorService } from 'apps/app-cic-ciam/src/services/oktafactor.service';
import { CodeInputComponent } from '../code-input/code-input.component';

const DEFAULT_ORIGINAL_URI = window.location.origin;

export enum EmailCodeMode {
  USER_UPDATE_2FA = 'user_update_2fa',
  VERIFICATION = 'verification',
  REGISTER = 'register',
  FORGOT_PASSWORD = 'forgot_password',
}

@Component({
  selector: 'ciam-email-code-widget',
  templateUrl: './email-code.component.html',
  styleUrls: ['./email-code.component.scss'],
})
export class WidgetEmailCodeComponent {
  @Input() account: string = '';
  user: string = '';
  credential = new EventEmitter<{ email: string; password: string }>();

  state: string = ''; //=WVNGL3grSERUdFJTVFNibFBsWG91cUZzMkxaUEgxMDBoczJncGlzWmdCUDI5Wkk2Nmx0bmM3end4YVJremtsUA&
  nonce: string = ''; //=tphLFY7GlJssurM4nh4s30rzhAMfciCk&
  code_challenge: string = ''; //=SDr1LUqoRgPsTxe336MVhzrRjhAGcWjnaj4W1bSw5Ew&
  code_challenge_method: string = ''; //=S256&
  client_id: string = ''; //=0oa4cd4j2gVdXd0Ac697&
  redirect_uri: string = ''; //=https%3A%2F%2Fonepass-sso-uat.cic.hk%2Foauth2%2Fv1%2Fauthorize%2Fcallback&
  response_type: string = ''; //=code&
  display: string = ''; //=page&
  scope: string = ''; //=email+openid+profile

  showModal: boolean = false;

  @Input() isRight: boolean = true;
  @Input() isFirstSent: boolean = true;
  @Input() mode: EmailCodeMode = EmailCodeMode.USER_UPDATE_2FA;

  uid: string = '';
  code: string = '';
  @Select(STATE_TOKEN) appState$!: Observable<StateModel>;
  emailEnrollResponse: any = null;

  isSms: boolean = false;
  isEmail: boolean = false;
  @Output() result = new EventEmitter<{ success: boolean }>();

  isLoadingSubmit: boolean = false;
  isLoadingResend: boolean = false;
  isSuccess: boolean = false;
  isError: boolean = false;

  faCircleCheck = faCircleCheck;
  faCircleXmark = faCircleXmark;
  faPenToSquare = faPenToSquare;
  @ViewChild('emailChangePopup')
  public emailChangePopup!: EmailChangePopupComponent;
  @ViewChild('codeInput')
  public codeInput?: CodeInputComponent;
  @Input() isAllowEmailChange: boolean = false;
  @Input() getRecaptchaTokens?: () => Promise<{ recaptchaV2Token: string; recaptchaV3Token: string }>;

  resendCooldown: number = 30;        // current cooldown in seconds
  resendCooldownInit: number = 30;    // cooldown in seconds
  resendCooldownTimer: any = null;    // timer for cooldown

  constructor(
    private router: Router,
    private translate: TranslateService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private route: ActivatedRoute,
    private http: HttpClient,
    private store: Store,
    private supportService: SupportService,
    private toastr: ToastrService,
    private selfFactorService: SelfFactorService,
    private oktaFactorService: OktaFactorService,
  ) {}

  ceil = Math.ceil;

  async ngOnInit() {
    if (!this.isFirstSent) {
      this.onResend(null);
    }
    this.appState$.subscribe(async (state) => {
      this.emailEnrollResponse = state.emailEnrollResponse;
      console.log(
        `emailEnrollResponse: ${JSON.stringify(this.emailEnrollResponse)}`
      );
    });
    this.resendCooldownTimer = setInterval(() => {
      if(this.resendCooldown > 0) {
        this.resendCooldown--;
      }
    }, 1000);
  }

  ngOnDestroy() {
    clearInterval(this.resendCooldownTimer);
  }

  onCodeChanged = ($event: any) => {
    this.code = $event;
  };

  onCodeCompleted = ($event: any) => {
    this.onSubmit($event);
  };

  onSubmit = async ($event: any) => {
    if (this.isLoadingSubmit) {
      return;
    }

    this.isLoadingSubmit = true;
    this.isError = false;
    let verification = (await firstValueFrom(this.appState$)).verification;
    let tempUserId = (await firstValueFrom(this.appState$)).tempUserId;

    let emailEnrollResponse = (await firstValueFrom(this.appState$))
      .emailEnrollResponse;

    let result = null;

    try {
      switch(this.mode) {
        case EmailCodeMode.USER_UPDATE_2FA:
          result = await this.selfFactorService.selfVerify2FaEmail(
            true,
            emailEnrollResponse.profile.email,
            this.code,
            true
          );
          break;
        case EmailCodeMode.VERIFICATION:
          result = await this.oktaFactorService.oktaVerify2FaEmail(
            false,
            this.code,
            verification?.userId ?? ''
          );
          break;
        case EmailCodeMode.REGISTER:
          result = await this.selfFactorService.selfVerify2FaEmail(
            false,
            emailEnrollResponse.profile.email,
            this.code,
            false
          );
          break;
        case EmailCodeMode.FORGOT_PASSWORD:
          result = await this.selfFactorService.selfVerify2FaEmail(
            false,
            emailEnrollResponse.profile.email,
            this.code,
            false
          );
          break;
      }

      if (result && result.result == true) {
        if (
          this.emailEnrollResponse != null &&
          this.emailEnrollResponse?.profile?.email != null
        ) {
          this.store.dispatch(
            new FactorInfoChange(this.emailEnrollResponse?.profile?.email)
          );
        }
        if (result.login) {
          this.store.dispatch(new FactorInfoChange(result.login));
        }
        if(result.token) {
          this.store.dispatch(new FactorEmailToken(result.token));
        }

        this.result.emit({ success: true });
        this.isSuccess = true;

        this.store.dispatch(new FactorInfoChangeConfirm());
      } else {
        this.isError = true;
      }
    } catch (e) {
      this.isError = true;
    }

    this.isLoadingSubmit = false;
  };

  onResend = async ($event: any) => {
    if (this.isLoadingResend) {
      return;
    }

    this.isLoadingResend = true;
    let verification = (await firstValueFrom(this.appState$)).verification;
    let tempUserId = (await firstValueFrom(this.appState$)).tempUserId;

    let emailEnrollResponse = (await firstValueFrom(this.appState$))
      .emailEnrollResponse;

    // Get fresh reCAPTCHA tokens if the callback is provided
    let recaptchaTokens: { recaptchaV2Token: string; recaptchaV3Token: string } = { recaptchaV2Token: '', recaptchaV3Token: '' };
    if (this.getRecaptchaTokens) {
      try {
        recaptchaTokens = await this.getRecaptchaTokens();
      } catch (e) {
        console.warn('[email-code] Failed to get reCAPTCHA tokens for resend:', e);
      }
    }

    try {
      let result = null;
      switch(this.mode) {
        case EmailCodeMode.USER_UPDATE_2FA:
          result = await this.selfFactorService.selfSend2faEmail(
            true,
            emailEnrollResponse.profile.email
          );
          break;
        case EmailCodeMode.VERIFICATION:
          result = await this.oktaFactorService.oktaSend2faEmail(
            false,
            this.translate.currentLang,
            verification?.userId ?? ''
          );
          break;
        case EmailCodeMode.REGISTER:
          result = await this.selfFactorService.selfSend2faEmail(
            false,
            emailEnrollResponse.profile.email,
            undefined,
            recaptchaTokens.recaptchaV2Token,
            recaptchaTokens.recaptchaV3Token
          );
          break;
        case EmailCodeMode.FORGOT_PASSWORD:
          result = await this.selfFactorService.selfSend2faEmail(
            false,
            emailEnrollResponse.profile.email,
            undefined,
            recaptchaTokens.recaptchaV2Token,
            recaptchaTokens.recaptchaV3Token
          );
          break;
      }

      this.isLoadingResend = false;

      // TODO: how to determine success?
      if ($event != null) {
        this.toastr.success(this.translate.instant('Email sent!'));
        this.resendCooldown = this.resendCooldownInit;

        // Clear the code input after resending
        this.code = '';
        if (this.codeInput && this.codeInput.reset) {
          this.codeInput.reset();
        }
      }
    } catch (e: any) {
      if (e?.status === 429) {
        this.toastr.error(
          this.translate.instant('Too many attempts. Please try again in a minute.')
        );
      } else {
        this.toastr.error(
          this.translate.instant('Failed to send email, please try again later.')
        );
      }
    }
    this.isLoadingResend = false;
  };

  emailMask(email: string) {
    // The enroll response may come from Factor/Get2faStatus, which no longer returns a profile.
    if (!email) return '';
    return email.replace(
      /(.{2})(.*)(?=@)/,
      (a, b, c) => b + '*'.repeat(c.length)
    );
  }

  onEditEmail = async ($event: any) => {
    this.emailChangePopup.requestPrompt();
  };
}
